/**
 * Location privacy for analytics.
 *
 * Trial Friend never stores a full ZIP code. A search row also carries the
 * condition, a device id, a timestamp and the IP city, and in a small town a
 * rare condition plus a 5-digit ZIP can point to one family. The first 3
 * digits (the HIPAA de-identification convention) still show roughly where
 * people search from. Place names people type ("louisiana", "boston") are
 * kept. Anything else with digits in it, such as a street address, is
 * dropped.
 *
 * Used by the browser tracker and again by /api/track, so an old cached
 * page that still sends a full ZIP is coarsened before it is stored.
 */

const LOCATION_KEYS = ["location", "zip", "zipCode", "zip_code", "postal", "postcode"];

/** A typed location reduced to something safe to store. */
export function coarseLocation(raw: unknown): string {
  const s = String(raw ?? "").trim();
  if (!s) return "";
  if (s === "any") return "any";
  if (/^\d{3}xx$/.test(s)) return s;
  const zip = s.match(/^(\d{3})\d{2}(?:-\d{4})?$/);
  if (zip) return `${zip[1]}xx`;
  if (/\d/.test(s)) return "other";
  return s.slice(0, 60);
}

/** A copy of meta with every location-like field coarsened. */
export function coarsenMeta<M extends Record<string, unknown> | null | undefined>(meta: M): M {
  if (!meta || typeof meta !== "object") return meta;
  const out: Record<string, unknown> = { ...meta };
  for (const k of LOCATION_KEYS) if (k in out) out[k] = coarseLocation(out[k]);
  return out as M;
}

/** A copy of meta with location-like fields removed. */
export function withoutLocation(meta: Record<string, unknown> | null | undefined): Record<string, unknown> {
  const out: Record<string, unknown> = { ...(meta || {}) };
  for (const k of LOCATION_KEYS) delete out[k];
  return out;
}
